import { useState } from "react";
import { Bouton } from "@/composants/ui/bouton";
import { ChampSelection, ChampTexte } from "@/composants/ui/champ";
import { Chargement } from "@/composants/ui/etats";
import { useChargement } from "@/lib/chargement";
import { api, type AffaireReconnue } from "@/lib/api";

/**
 * Regrouper la commande dans une autre affaire du même client (ADR-0032).
 *
 * Le cas : un chargé d'affaires n'avait pas reconnu le client d'un collègue et
 * a ouvert une seconde affaire. Les affaires proposées sont toutes celles du
 * client, qu'on les voie ou non : le même client suffit, comme au rattachement.
 * L'affaire laissée vide disparaît ; son numéro ne revient pas.
 */
export function FormulaireRegroupement({
  clientId,
  affaireActuelle,
  onValider,
  onAnnuler,
}: {
  clientId: string;
  affaireActuelle: string;
  onValider: (affaireId: string, motif: string | null) => Promise<void>;
  onAnnuler: () => void;
}) {
  const affaires = useChargement<AffaireReconnue[]>(
    (signal) => api.affairesDuClient(clientId, signal),
    [clientId],
  );
  const [affaireId, setAffaireId] = useState("");
  const [motif, setMotif] = useState("");
  const [envoi, setEnvoi] = useState(false);
  const autres = (affaires.donnees ?? []).filter((affaire) => affaire.id !== affaireActuelle);

  if (affaires.enCours) return <Chargement message="Recherche des affaires du client…" />;
  if (autres.length === 0) {
    return (
      <div className="flex flex-col gap-3">
        <p className="text-sm text-encre-douce">
          Ce client n'a pas d'autre affaire : il n'y a rien à regrouper.
        </p>
        <Bouton type="button" variante="discret" className="self-start" onClick={onAnnuler}>
          Fermer
        </Bouton>
      </div>
    );
  }
  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={async (evenement) => {
        evenement.preventDefault();
        if (!affaireId) return;
        setEnvoi(true);
        try {
          await onValider(affaireId, motif.trim() || null);
        } finally {
          setEnvoi(false);
        }
      }}
    >
      <ChampSelection
        libelle="Affaire d'arrivée"
        required
        options={[
          { valeur: "", libelle: "— Choisir —" },
          ...autres.map((affaire) => ({ valeur: affaire.id, libelle: affaire.numero })),
        ]}
        value={affaireId}
        onChange={(e) => setAffaireId(e.target.value)}
      />
      <ChampTexte
        libelle="Motif (facultatif)"
        value={motif}
        maxLength={1000}
        onChange={(e) => setMotif(e.target.value)}
      />
      <p className="text-xs text-encre-douce">
        Si l'affaire actuelle ne regroupe que cette commande, elle est supprimée ; son numéro
        n'est jamais réutilisé. Le regroupement est historisé.
      </p>
      <div className="flex flex-wrap gap-2">
        <Bouton type="submit" variante="principal" disabled={envoi || !affaireId}>
          {envoi ? "Regroupement…" : "Regrouper"}
        </Bouton>
        <Bouton type="button" variante="discret" onClick={onAnnuler} disabled={envoi}>
          Annuler
        </Bouton>
      </div>
    </form>
  );
}
